
import { api } from "../../api/config";
import { createOrderItems } from "../../api/useCreateOrder";
import { RecordContextType } from "../../components/record/Record";
import { queryClient } from "../../main";
import { getStore } from "../../store/store";
import { MAKE_WEBHOOKS, PAYMENTS_STAFF_ROLE_IDS } from "../../utils/constants";
import {
    validateNoExistingInvoice,
    validateNoExistingInvoicing,
    validateOrderItemsAgreement,
    buildInvoiceAlerts,
    handleInvoiceAlert,
    submitInvoice
} from "./ordersActionsUtils";
import { getOrderInvoices } from "../../api/moloni/useOrderInvoice";
import { getInvoiceSchedules } from "../../api/moloni/useInvoiceSchedules";
import { calculateInvoicedNet } from "./moloni/invoiceDocumentsUtils";
import { TerminalPaymentDialog } from "./terminal-payment/TerminalPaymentDialog";
import { roundOrderTotal } from "./terminal-payment/terminalPaymentAmount";
import { MbwayPaymentDialog } from "./mbway-payment/MbwayPaymentDialog";
import { readMbwayPrefillSources } from "./mbway-payment/mbwayPhone";

export const actions = [
    {
        code: "create_invoice",
        name: "Criar Fatura",
        actionFn: (context: any, executeAction: any) => createInvoice(context),
    },
    {
        code: "create_invoice_without_nif",
        name: "Criar Fatura (SEM NIF)",
        actionFn: (context: any, executeAction: any) => createInvoice(context, true),
    },
    {
        code: "create_receipt",
        name: "Criar Recibo",
        actionFn: (context: any, executeAction: any) => createReceipt(context),
    },
    {
        code: "get_invoice",
        name: "Ver fatura",
        actionFn: (context: any, executeAction: any) => getInvoice(context),
    },
    {
        code: "send_payment_link_email",
        name: "Enviar link de pagamento por email",
        actionFn: (context: any, executeAction: any) => sendPaymentLinkEmail(context),
    },
    {
        code: "charge_on_terminal",
        name: "Cobrar no terminal",
        roles: PAYMENTS_STAFF_ROLE_IDS,
        actionFn: (context: any) => openTerminalPaymentDialog(context),
    },
    {
        code: "charge_by_mbway",
        name: "Cobrar por MB WAY",
        roles: PAYMENTS_STAFF_ROLE_IDS,
        actionFn: (context: any) => openMbwayPaymentDialog(context),
    },
    {
        code: "duplicate_order",
        name: "Duplicar Compra",
        actionFn: (context: any, executeAction: any) => duplicateOrder(context),
    }
];

const showValidationError = (message: string) => {
    const { notifications } = getStore();
    notifications.setNotification({ message, isOpen: true, severity: "error" });
};

export const createInvoice = async (context: RecordContextType, withoutNif?: boolean) => {
    const { notifications } = getStore();
    const cachedUser = queryClient.getQueryData(["user"]) as any;

    const invoiceValidation = validateNoExistingInvoice(context?.item?.invoice_id);
    if (!invoiceValidation.isValid) {
        showValidationError(invoiceValidation.errorMessage!);
        return;
    }

    const orderItems = context?.item?.order_items || [];
    const agreementValidation = validateOrderItemsAgreement(orderItems);
    if (!agreementValidation.isValid) {
        showValidationError(agreementValidation.errorMessage!);
        return;
    }

    let existingInvoicingValidation;
    try {
        const [schedules, invoiceDocuments] = await Promise.all([
            getInvoiceSchedules(context?.item?.id, true),
            getOrderInvoices(context?.item?.id),
        ]);
        existingInvoicingValidation = validateNoExistingInvoicing(schedules, calculateInvoicedNet(invoiceDocuments));
    } catch (error) {
        showValidationError("Não foi possível verificar a faturação existente desta compra. Tente novamente.");
        return;
    }
    if (!existingInvoicingValidation.isValid) {
        showValidationError(existingInvoicingValidation.errorMessage!);
        return;
    }

    const invoiceAlerts = buildInvoiceAlerts({
        insuranceNumber: context?.item?.treatment?.client?.insurance_number,
        orderItems,
        priceNotes: context?.item?.treatment?.price_notes,
        nextStep: context?.item?.treatment?.next_step,
    });
    if (invoiceAlerts.length > 0) {
        const shouldProceed = await handleInvoiceAlert(invoiceAlerts);
        if (!shouldProceed) return;
    } else {
        const confirmed = window.confirm("De certeza que deseja criar uma fatura para esta compra?");
        if (!confirmed) return;
    }

    if (!withoutNif) {
        const missingFields = areAllRequiredFieldsFilled(context);
        if (missingFields) {
            showValidationError(missingFields);
            return;
        }
    }

    try {
        await submitInvoice(context, withoutNif || false, cachedUser);
    } catch (error: any) {
        notifications.setNotification({ message: "Erro ao criar fatura", isOpen: true, severity: "error" });
        throw new Error(error.response.data);
    }
};

const getInvoice = async (context: RecordContextType) => {
    const { notifications } = getStore();
    try {
        const response = await api({
            method: "POST",
            url: "https://hook.eu1.make.com/ybgubakz5ybmhw9hdezm44duy4vh2b6l",
            data: context.item,
        });
        const invoiceUrl = response.data;
        window.open(invoiceUrl, "_blank");
    } catch (error: any) {
        notifications.setNotification({
            message: "Erro ao criar fatura",
            isOpen: true,
            severity: "error",
        });
        throw new Error(error.response.data);
    }
};



const areAllRequiredFieldsFilled = (context: RecordContextType) => {
    const requiredFields = [
        { field: "vat", label: "NIF" },
        { field: "invoice_email", label: "Email de faturação" },
    ];
    const client = context.item.treatment.client;

    const missingFields = requiredFields
        .filter((field) => {
            return !client[field.field];
        })
        .map((field) => field.label);

    if (missingFields.length > 0) {
        const missingFieldsMessage = `Os seguintes campos são obrigatórios: ${missingFields.join(", ")}`;
        return missingFieldsMessage;
    }

    return null;
};

const sendPaymentLinkEmail = async (context: RecordContextType) => {
    const { notifications } = getStore();

    const confirmed = window.confirm("De certeza que deseja enviar o link de pagamento para o email do cliente?");
    if (!confirmed) return;

    const order = context.item;
    if (order.payment_status === "paid") {
        notifications.setNotification({
            message: "A compra já está paga",
            isOpen: true,
            severity: "error",
        });
        return;
    }

    try {
        await api({
            method: "POST",
            url: MAKE_WEBHOOKS.send_payment_link_email,
            data: order,
        });
        notifications.setNotification({
            message: "Link de pagamento enviado com sucesso para o email do cliente",
            isOpen: true,
            severity: "success",
        });
    }catch (error: any) {
        notifications.setNotification({
            message: "Erro ao enviar link de pagamento",
            isOpen: true,
            severity: "error",
        });
        throw new Error(error.response.data);
    }
}

const refreshOrderQueries = () => {
    queryClient.invalidateQueries({
        predicate: (query: any) => {
            const isOrderQuery = String(query.queryKey[0]).startsWith("orders");
            return isOrderQuery;
        },
    });
};

const openTerminalPaymentDialog = (context: RecordContextType) => {
    const order = context.item;
    if (order.payment_status === "paid") {
        showValidationError("A compra já está paga");
        return;
    }

    const { appDialog } = getStore();
    appDialog.createDialog({
        title: "Cobrar no terminal",
        contentAndActions: (
            <TerminalPaymentDialog
                orderId={order.id}
                clinicName={order.clinic?.name ?? ""}
                orderTotal={roundOrderTotal(order.order_items || [])}
                onPaid={refreshOrderQueries}
                onClose={() => appDialog.toggleIsOpen()}
            />
        ),
    });

    const actionResult = { showSuccessMessage: "no" };
    return actionResult;
};

const openMbwayPaymentDialog = (context: RecordContextType) => {
    const order = context.item;
    if (order.payment_status === "paid") {
        showValidationError("A compra já está paga");
        return;
    }

    const { clientPhone, treatmentId } = readMbwayPrefillSources(order);
    const { appDialog } = getStore();
    appDialog.createDialog({
        title: "Cobrar por MB WAY",
        contentAndActions: (
            <MbwayPaymentDialog
                orderId={order.id}
                orderTotal={roundOrderTotal(order.order_items || [])}
                clientPhone={clientPhone}
                treatmentId={treatmentId}
                onPaid={refreshOrderQueries}
                onClose={() => appDialog.toggleIsOpen()}
            />
        ),
    });

    const actionResult = { showSuccessMessage: "no" };
    return actionResult;
};

// const openPaymentDialog = async (context: RecordContextType) => {
//     const orderId = context.item.id;
//     const paymentLink = await createPaymentLink(orderId);

//     const { appDialog } = getStore();

//     appDialog.createDialog({
//         title: "Enviar link de pagamento",
//         contentAndActions: React.createElement(PaymentDialog, {
//             context: context,
//             paymentLink: paymentLink.external.RedirectUrl,
//             onClose: () => appDialog.toggleIsOpen(),
//         }),
//         allowSoftCancel: true,
//         maxWidth: "lg",
//     });

//     return {
//         showSuccessMessage: "no",
//     };
// };

// const createPaymentLink = async (orderId: number) => {
//     try {
//         const response = await api({
//             method: "POST",
//             url: "/generate-payment-link",
//             data: {
//                 order_id: orderId,
//                 description: "",
//             },
//         });
//         const items = response.data;
//         return items;
//     } catch (error: any) {
//         throw new Error(error.response.data || "Servidor indisponível");
//     }
// };

const createReceipt = async (context: RecordContextType) => {
    const { notifications } = getStore();

    const hasReceipt = context?.item?.receipt_number;

    if (hasReceipt) {
        notifications.setNotification({
            message: "Já existe um recibo associado a esta compra",
            isOpen: true,
            severity: "error",
        });
        return;
    }

    const confirmed = window.confirm("De certeza que deseja criar um recibo para esta compra?");
    if (!confirmed) return;

    try {
        await api({
            method: "POST",
            url: MAKE_WEBHOOKS.create_receipt,
            data: context.item,
        });
        notifications.setNotification({
            message: "Recibo criado e enviado por email ao cliente com sucesso",
            isOpen: true,
            severity: "success",
        });
    } catch (error: any) {
        notifications.setNotification({
            message: "Erro ao criar recibo, verifique se o cliente tem fatura associada",
            isOpen: true,
            severity: "error",
        });
        throw new Error(error.response.data);
    }
};

const duplicateOrder = async (context: RecordContextType) => {
    const { notifications } = getStore();
    const orderId = context.item.id;

    const confirmed = window.confirm("Tem a certeza que deseja duplicar esta compra?");
    if (!confirmed) return;

    try {
        const orderDetailsResponse = await api({
            method: "GET",
            url: `items/orders/${orderId}`,
            params: {
                fields: "*,order_items.*",
            },
        });
        const order = orderDetailsResponse.data.data;

        const newOrderData = {
            clinic: order.clinic,
            treatment: order.treatment,
            payment_status: "not_paid",
            order_date: new Date().toISOString(),
            notes: order.notes || "",
        };

        const orderResponse = await api({
            method: "POST",
            url: "items/orders",
            data: newOrderData,
        });
        const newOrderId = orderResponse.data.data.id;

        const apiOrderItems = order.order_items || [];
        const orderItemsForCreate = apiOrderItems.map((item: any) => ({
            variantId: item.service_variant,
            quantity: item.quantity,
            price: item.price,
            discount: item.discount,
        }));

        await createOrderItems(newOrderId, { order_items: orderItemsForCreate });

        queryClient.invalidateQueries({
            predicate(query: any) {
                return query.queryKey[0].startsWith("orders");
            },
        });

        notifications.setNotification({
            message: "Compra duplicada com sucesso",
            isOpen: true,
            severity: "success",
        });

        window.location.href = `/orders/${newOrderId}/update`;
    } catch (error: any) {
        notifications.setNotification({
            message: "Erro ao duplicar compra",
            isOpen: true,
            severity: "error",
        });
        throw new Error(error.response?.data || "Erro desconhecido");
    }
};
